import { useEffect, useState } from 'react';
import { MoneyInput, Field, Select, TextInput } from '../../shell/kit';
import { MultiPicker } from '../../shell/pickers';
import { ruleOptions, type RuleOptions } from './api';
import type { RuleDraft, RuleView } from './api';
import type { LoyaltyTranslate } from './i18n';

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export interface RuleFormProps {
  readonly t: LoyaltyTranslate;
  readonly initial?: RuleView | undefined;
  readonly busy: boolean;
  readonly onSubmit: (draft: RuleDraft) => void;
  readonly onCancel: () => void;
}

type ActionKind = 'fixed' | 'percent' | 'multiplier' | 'burn';

function number(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : undefined;
}

function asList(value: unknown): readonly string[] {
  return Array.isArray(value) ? (value as string[]) : [];
}

function text(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

const FIELD = 'w-full rounded border border-line-strong px-2 py-1 text-sm';
const LABEL = 'mb-1 block text-xs text-ink-muted';

export function RuleForm({ t, initial, busy, onSubmit, onCancel }: RuleFormProps) {
  const condition = (initial?.condition ?? {}) as Record<string, unknown>;
  const action = (initial?.action ?? {}) as Record<string, unknown>;

  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [priority, setPriority] = useState(String(initial?.priority ?? 100));
  const [siteIds, setSiteIds] = useState<readonly string[]>(initial?.siteIds ?? []);

  const [services, setServices] = useState<readonly string[]>(asList(condition['serviceCodes']));
  const [segments, setSegments] = useState<readonly string[]>(asList(condition['segmentCodes']));

  /*
   * Значения для трёх списков. Раньше их не было вовсе: объекты вводились
   * идентификаторами, услуги и сегменты — кодами по памяти.
   */
  const [options, setOptions] = useState<RuleOptions | null>(null);
  useEffect(() => {
    let alive = true;
    ruleOptions()
      .then((value) => {
        if (alive) setOptions(value);
      })
      .catch(() => {
        if (alive) setOptions({ sites: [], services: [], segments: [] });
      });
    return () => {
      alive = false;
    };
  }, []);
  // Суммы держим в тиынах, а показываем в тенге: конструктор правил был
  // последним местом, где деньги вводились сырыми минорными единицами.
  const [minAmount, setMinAmount] = useState<number | null>(
    number(text(condition['minAmountMinor'])) ?? null,
  );
  const [maxAmount, setMaxAmount] = useState<number | null>(
    number(text(condition['maxAmountMinor'])) ?? null,
  );
  const [days, setDays] = useState<readonly number[]>(
    Array.isArray(condition['daysOfWeek']) ? (condition['daysOfWeek'] as number[]) : [],
  );
  const [timeFrom, setTimeFrom] = useState(text(condition['timeFrom']));
  const [timeTo, setTimeTo] = useState(text(condition['timeTo']));
  const [visitFrom, setVisitFrom] = useState(text(condition['visitNumberFrom']));
  const [visitTo, setVisitTo] = useState(text(condition['visitNumberTo']));

  const [kind, setKind] = useState<ActionKind>((action['kind'] as ActionKind) ?? 'percent');
  const [amount, setAmount] = useState<number | null>(number(text(action['amountMinor'])) ?? null);
  const [percent, setPercent] = useState(
    action['percentBp'] === undefined ? '5' : String(Number(action['percentBp']) / 100),
  );
  const [factor, setFactor] = useState(
    action['factorBp'] === undefined ? '2' : String(Number(action['factorBp']) / 10000),
  );
  const [expires, setExpires] = useState(text(action['expiresInDays']));
  // Правило списания: доля чека в процентах, курс в тенге за бонус, порог в тиынах.
  const [burnShare, setBurnShare] = useState(
    action['maxShareBp'] === undefined ? '50' : String(Number(action['maxShareBp']) / 100),
  );
  const [burnRate, setBurnRate] = useState(
    action['rateBp'] === undefined ? '1' : String(Number(action['rateBp']) / 10000),
  );
  const [burnMin, setBurnMin] = useState<number | null>(
    number(text(action['minBalanceMinor'])) ?? null,
  );

  function toggleDay(day: number): void {
    setDays((current) =>
      current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort(),
    );
  }

  function buildCondition(): Record<string, unknown> {
    const built: Record<string, unknown> = {};
    if (services.length > 0) built['serviceCodes'] = [...services];
    if (segments.length > 0) built['segmentCodes'] = [...segments];
    if (minAmount !== null) built['minAmountMinor'] = minAmount;
    if (maxAmount !== null) built['maxAmountMinor'] = maxAmount;
    if (days.length > 0) built['daysOfWeek'] = days;
    if (timeFrom && timeTo) {
      built['timeFrom'] = timeFrom;
      built['timeTo'] = timeTo;
    }
    if (number(visitFrom) !== undefined) built['visitNumberFrom'] = number(visitFrom);
    if (number(visitTo) !== undefined) built['visitNumberTo'] = number(visitTo);
    return built;
  }

  function buildAction(): Record<string, unknown> {
    if (kind === 'fixed') {
      return {
        kind,
        amountMinor: amount ?? 0,
        ...(number(expires) === undefined ? {} : { expiresInDays: number(expires) }),
      };
    }
    if (kind === 'percent') {
      return {
        kind,
        percentBp: Math.round(Number(percent || '0') * 100),
        ...(number(expires) === undefined ? {} : { expiresInDays: number(expires) }),
      };
    }
    if (kind === 'burn') {
      return {
        kind,
        maxShareBp: Math.round(Number(burnShare.replace(',', '.') || '0') * 100),
        rateBp: Math.round(Number(burnRate.replace(',', '.') || '0') * 10000),
        ...(burnMin === null || burnMin === 0 ? {} : { minBalanceMinor: burnMin }),
      };
    }
    return { kind, factorBp: Math.round(Number(factor || '0') * 10000) };
  }

  return (
    <form
      className="space-y-4 rounded border border-line bg-surface p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          code: code.trim(),
          name: name.trim(),
          condition: buildCondition(),
          action: buildAction(),
          priority: number(priority) ?? 100,
          siteIds: [...siteIds],
        });
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('rules.code')}>
          <TextInput value={code} required onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label={t('rules.name')}>
          <TextInput value={name} required onChange={(e) => setName(e.target.value)} />
        </Field>
        {/* Приоритет решает, сработает ли правило вообще (QA.md, Q9). */}
        <Field label={t('rules.priority')} hint={t('rules.priority.hint')}>
          <TextInput
            value={priority}
            inputMode="numeric"
            onChange={(e) => setPriority(e.target.value)}
          />
        </Field>
      </div>

      <Field label={t('rules.sites')}>
        <MultiPicker
          options={options?.sites ?? []}
          value={siteIds}
          onChange={setSiteIds}
          loading={options === null}
          emptyMeans={t('rules.sites.all')}
        />
      </Field>

      <fieldset className="rounded border border-line p-3">
        <legend className="px-1 text-sm font-medium text-ink">{t('rules.condition')}</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('rules.condition.services')}>
            <MultiPicker
              options={options?.services ?? []}
              value={services}
              onChange={setServices}
              loading={options === null}
              emptyMeans={t('rules.condition.services.all')}
            />
          </Field>
          <Field label={t('rules.condition.segments')}>
            <MultiPicker
              options={options?.segments ?? []}
              value={segments}
              onChange={setSegments}
              loading={options === null}
              emptyMeans={t('rules.condition.segments.all')}
            />
          </Field>
          <label>
            <span className={LABEL}>{t('rules.condition.minAmount')}</span>
            <MoneyInput valueMinor={minAmount} onChangeMinor={setMinAmount} />
          </label>
          <label>
            <span className={LABEL}>{t('rules.condition.maxAmount')}</span>
            <MoneyInput valueMinor={maxAmount} onChangeMinor={setMaxAmount} />
          </label>
          <Field label={t('rules.condition.timeFrom')}>
            <TextInput
              value={timeFrom}
              placeholder="09:00"
              onChange={(e) => setTimeFrom(e.target.value)}
            />
          </Field>
          <Field label={t('rules.condition.timeTo')}>
            <TextInput
              value={timeTo}
              placeholder="18:00"
              onChange={(e) => setTimeTo(e.target.value)}
            />
          </Field>
          <Field label={t('rules.condition.visitFrom')}>
            <TextInput
              value={visitFrom}
              inputMode="numeric"
              onChange={(e) => setVisitFrom(e.target.value)}
            />
          </Field>
          <Field label={t('rules.condition.visitTo')}>
            <TextInput
              value={visitTo}
              inputMode="numeric"
              onChange={(e) => setVisitTo(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-3">
          <span className={LABEL}>{t('rules.condition.days')}</span>
          <div className="flex flex-wrap gap-1">
            {DAY_LABELS.map((label, index) => {
              const day = index + 1;
              const on = days.includes(day);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleDay(day)}
                  className={`rounded border px-2 py-1 text-xs ${
                    on
                      ? 'border-brand-700 bg-brand-600 text-ink-invert'
                      : 'border-line-strong text-ink-muted'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </fieldset>

      <fieldset className="rounded border border-line p-3">
        <legend className="px-1 text-sm font-medium text-ink">{t('rules.action')}</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('rules.action.kind')}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as ActionKind)}>
              <option value="percent">{t('rules.action.percent')}</option>
              <option value="fixed">{t('rules.action.fixed')}</option>
              <option value="multiplier">{t('rules.action.multiplier')}</option>
              <option value="burn">{t('rules.action.burn')}</option>
            </Select>
          </Field>

          {kind === 'fixed' && (
            <label>
              <span className={LABEL}>{t('rules.action.amount')}</span>
              <MoneyInput valueMinor={amount} onChangeMinor={setAmount} />
            </label>
          )}

          {kind === 'percent' && (
            <Field label={t('rules.action.percentValue')}>
              <TextInput
                value={percent}
                inputMode="decimal"
                onChange={(e) => setPercent(e.target.value)}
              />
            </Field>
          )}

          {kind === 'multiplier' && (
            <Field label={t('rules.action.factor')}>
              <TextInput
                value={factor}
                inputMode="decimal"
                onChange={(e) => setFactor(e.target.value)}
              />
            </Field>
          )}

          {kind === 'burn' && (
            <>
              <Field label={t('rules.action.burnShare')}>
                <TextInput
                  value={burnShare}
                  inputMode="decimal"
                  onChange={(e) => setBurnShare(e.target.value)}
                />
              </Field>
              <Field label={t('rules.action.burnRate')}>
                <TextInput
                  value={burnRate}
                  inputMode="decimal"
                  onChange={(e) => setBurnRate(e.target.value)}
                />
              </Field>
              <label>
                <span className={LABEL}>{t('rules.action.burnMin')}</span>
                <MoneyInput valueMinor={burnMin} onChangeMinor={setBurnMin} />
              </label>
              <p className="text-xs text-ink-muted sm:col-span-2">
                {t('rules.action.describeBurn', { share: burnShare || '0', rate: burnRate || '0' })}.{' '}
                {t('rules.action.burnHint')}
              </p>
            </>
          )}

          {(kind === 'fixed' || kind === 'percent') && (
            <Field label={t('rules.action.expires')}>
              <TextInput
                value={expires}
                inputMode="numeric"
                onChange={(e) => setExpires(e.target.value)}
              />
            </Field>
          )}
        </div>
      </fieldset>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
        >
          {t('rules.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
